import {
  CHECKLIST_LIMITS,
  validateChecklistAnswers,
  type ChecklistAnswer,
  type ChecklistIssue,
  type ChecklistIssueCode,
} from "@fts/core";
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { ChecklistInvalidError, type ChecklistForm as FormData, type ChecklistFormItem } from "../data/provider";
import { LANGS, useI18n } from "../i18n";
import { errorCode, errorKey } from "../lib/errors";
import { formatAlarmTime } from "../lib/alarms";
import { useSession } from "../state/session";
import { itemText } from "./ChecklistParts";

/**
 * Public pre-shift checklist (ADR 0016), opened from the QR label on a vehicle: `#/c/<token>`.
 * Works without a console login, mobile first: one item per row, three big answer buttons, note and
 * photo only when needed, sticky submit bar. The operator never sees a warning or a "critical" mark
 * and the result is not shown: a critical failure only notifies the people chosen for the site, and
 * the vehicle is never locked (ADR 0003, ADR 0013).
 */

type PhotoState = { localId: string; id: string | null; preview: string | null; uploading: boolean; error: boolean };
type Answer = { answer: ChecklistAnswer | null; note: string; photos: PhotoState[] };

const ANSWERS: ChecklistAnswer[] = ["ok", "not_ok", "na"];
let localSeq = 0;

function newKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Always re-encode to a ≤ 1600 px JPEG when the browser can: smaller uploads, and the new file carries no
 * Exif/location metadata (the Worker strips metadata as well).
 */
async function shrinkImage(file: File): Promise<Blob> {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.82));
    return blob ?? file;
  } catch {
    return file;
  }
}

function previewUrl(file: Blob): string | null {
  try {
    return typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null;
  } catch {
    return null;
  }
}

export function ChecklistFormPage({ token }: { token: string }) {
  const { t, lang, setLang, locale } = useI18n();
  const { provider } = useSession();
  const [form, setForm] = useState<FormData | "loading" | { error: string }>("loading");
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [issues, setIssues] = useState<ChecklistIssue[]>([]);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [done, setDone] = useState<{ at: string } | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  // One key per form load: a retried or double-tapped submit returns the same submission (idempotent).
  const clientKey = useRef<string>(newKey());
  // Set synchronously on the first tap, before React re-renders the disabled button.
  const submitting = useRef(false);
  const [waitPhotos, setWaitPhotos] = useState(false);
  // The template changed after the form was loaded (QA-7A-08): reload, keeping answers of unchanged items.
  const [formChanged, setFormChanged] = useState(false);

  /** `keep`: answers to carry over (form_changed reload): kept for items that still exist, unchanged. */
  const load = useCallback(
    async (keep?: { answers: Record<string, Answer>; items: ChecklistFormItem[] }) => {
      setForm("loading");
      setFormChanged(false);
      try {
        const f = await provider.getChecklistForm(token);
        setForm(f);
        // Keep an answer only when the item is the same (id and texts and photo rule unchanged).
        const same = (i: ChecklistFormItem) => {
          const old = keep?.items.find((x) => x.id === i.id);
          return !!old && old.textTr === i.textTr && old.textEn === i.textEn && old.photoRequiredOnFail === i.photoRequiredOnFail && old.naAllowed === i.naAllowed;
        };
        setAnswers(Object.fromEntries(f.items.map((i) => [i.id, (same(i) && keep?.answers[i.id]) || { answer: null, note: "", photos: [] }])));
        clientKey.current = newKey();
        setIssues([]);
      } catch (e) {
        setForm({ error: errorCode(e) });
      }
    },
    [provider, token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    document.title = `${t("cf.title")} · ${t("app.productLabel")}`;
  }, [t]);

  const items = typeof form === "object" && "items" in form ? form.items : [];
  const doneCount = items.filter((i) => answers[i.id]?.answer).length;
  const issueMap = useMemo(() => {
    const m = new Map<string, ChecklistIssueCode[]>();
    for (const x of issues) if (x.itemId) m.set(x.itemId, [...(m.get(x.itemId) ?? []), x.code]);
    return m;
  }, [issues]);

  const patch = (id: string, p: Partial<Answer> | ((a: Answer) => Partial<Answer>)) =>
    setAnswers((s) => {
      const cur = s[id] ?? { answer: null, note: "", photos: [] };
      return { ...s, [id]: { ...cur, ...(typeof p === "function" ? p(cur) : p) } };
    });

  // Clear an item's issues as soon as the operator touches it.
  const touch = (id: string) => setIssues((xs) => (xs.some((x) => x.itemId === id) ? xs.filter((x) => x.itemId !== id) : xs));

  const addPhoto = async (item: ChecklistFormItem, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const localId = `p${++localSeq}`;
    const blob = await shrinkImage(file);
    patch(item.id, (a) => ({ photos: [...a.photos, { localId, id: null, preview: previewUrl(blob), uploading: true, error: false }] }));
    touch(item.id);
    try {
      const id = await provider.uploadChecklistPhoto(token, blob);
      patch(item.id, (a) => ({ photos: a.photos.map((p) => (p.localId === localId ? { ...p, id, uploading: false } : p)) }));
      setWaitPhotos(false);
    } catch {
      patch(item.id, (a) => ({ photos: a.photos.map((p) => (p.localId === localId ? { ...p, uploading: false, error: true } : p)) }));
    }
  };

  const submit = async () => {
    if (typeof form !== "object" || !("items" in form) || busy || submitting.current) return;
    const payload = form.items.map((i) => {
      const a = answers[i.id]!;
      return {
        itemId: i.id,
        answer: (a.answer ?? "") as ChecklistAnswer,
        note: a.note.trim() || null,
        photoIds: a.photos.flatMap((p) => (p.id ? [p.id] : [])),
      };
    });
    const local = validateChecklistAnswers(
      form.items.map((i) => ({ id: i.id, critical: false, photoRequiredOnFail: i.photoRequiredOnFail, naAllowed: i.naAllowed })),
      payload.filter((p) => p.answer),
    );
    if (local.length > 0) {
      setIssues(local);
      const first = local[0]?.itemId;
      if (first) document.getElementById(`cf-item-${first}`)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      return;
    }
    if (form.items.some((i) => answers[i.id]?.photos.some((p) => p.uploading))) {
      setWaitPhotos(true);
      return;
    }
    setWaitPhotos(false);
    submitting.current = true;
    setBusy(true);
    setSubmitError(null);
    try {
      await provider.submitChecklist(token, { locale: lang, answers: payload, clientSubmissionId: clientKey.current, formVersion: form.version });
      setDone({ at: new Date().toISOString() });
      document.documentElement.scrollTop = 0;
    } catch (e) {
      if (e instanceof ChecklistInvalidError) setIssues(e.issues);
      else if (errorCode(e) === "form_changed") setFormChanged(true);
      else setSubmitError(e);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const langSwitch = (
    <div className="cf-lang" role="group" aria-label={t("cf.language")}>
      {LANGS.map((l) => (
        <button key={l} type="button" className={l === lang ? "cf-lang-btn is-active" : "cf-lang-btn"} aria-pressed={l === lang} onClick={() => setLang(l)}>
          {t(`lang.${l}`)}
        </button>
      ))}
    </div>
  );

  const shell = (body: ReactNode) => (
    <div className="cf-page">
      <header className="cf-header">
        <div className="cf-header-row">
          <span className="cf-product">{t("app.productLabel")}</span>
          {langSwitch}
        </div>
        <h1 className="cf-title">{t("cf.title")}</h1>
        {provider.mode === "demo" && <span className="badge tone-accent">{t("app.demoBadge")}</span>}
      </header>
      <main className="cf-main">{body}</main>
    </div>
  );

  if (form === "loading")
    return shell(
      <div className="loading" role="status">
        <span className="spinner" aria-hidden="true" />
        {t("common.loading")}
      </div>,
    );
  if ("error" in form) {
    const msg =
      form.error === "checklist_token_invalid" ? t("cf.invalid")
      : form.error === "rate_limited" ? t("cf.rateLimited")
      : form.error === "network" ? t("cf.offline")
      : t("cf.loadError");
    return shell(
      <div className="cf-message" role="alert">
        <Icon name="alert" size={22} />
        <p>{msg}</p>
        {form.error !== "checklist_token_invalid" && (
          <button type="button" className="button button-secondary" onClick={() => void load()}>
            {t("common.retry")}
          </button>
        )}
      </div>,
    );
  }

  const vehicleLine = (
    <div className="cf-vehicle">
      <span className="cf-fleet mono">{form.vehicle.fleetNo}</span>
      {form.vehicle.siteName && <span className="cf-site">{form.vehicle.siteName}</span>}
      <span className="cf-shift">{form.shift ? t("cf.shift", { name: form.shift.name }) : t("cf.noShift")}</span>
    </div>
  );

  if (done)
    return shell(
      <>
        {vehicleLine}
        <div className="cf-message cf-done" role="status">
          <Icon name="check" size={26} />
          <p className="strong">{t("cf.done")}</p>
          <p className="muted">{t("cf.doneDetail", { fleetNo: form.vehicle.fleetNo, time: formatAlarmTime(done.at, locale) ?? "" })}</p>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => {
              setDone(null);
              void load();
            }}
          >
            {t("cf.again")}
          </button>
        </div>
      </>,
    );

  const missing = issues.filter((x) => x.itemId).length;
  return shell(
    <>
      {vehicleLine}
      {formChanged && (
        <div className="cf-message" role="alert">
          <Icon name="refresh" size={22} />
          <p>{t("cf.formChanged")}</p>
          <button type="button" className="button button-primary" onClick={() => void load({ answers, items: form.items })}>
            {t("cf.reload")}
          </button>
        </div>
      )}
      {form.submittedThisShift && <p className="cf-info">{t("cf.alreadySubmitted")}</p>}
      {form.openFaults.length > 0 && (
        <section className="cf-faults" aria-labelledby="cf-faults-title">
          <h2 id="cf-faults-title" className="cf-section-title">
            {t("cf.openFaults")}
          </h2>
          <ul>
            {form.openFaults.map((f, n) => (
              <li key={`${f.code}-${n}`}>
                <span className="mono">{f.code}</span> {itemText(lang, f)} <span className="muted small">· {formatAlarmTime(f.createdAt, locale)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <p className="cf-intro">{t("cf.intro")}</p>
      <ol className="cf-items" ref={listRef}>
        {form.items.map((item, idx) => {
          const a = answers[item.id] ?? { answer: null, note: "", photos: [] };
          const errs = issueMap.get(item.id) ?? [];
          const failed = a.answer === "not_ok";
          const noteId = `cf-note-${item.id}`;
          const errId = `cf-err-${item.id}`;
          return (
            <li key={item.id} id={`cf-item-${item.id}`} className={`cf-item${errs.length ? " has-issue" : ""}${failed ? " is-failed" : ""}`}>
              <div className="cf-item-head" id={`cf-label-${item.id}`}>
                <span className="cf-num" aria-hidden="true">
                  {idx + 1}
                </span>
                <span className="cf-text">{itemText(lang, item)}</span>
              </div>
              <div className="cf-answers" role="radiogroup" aria-labelledby={`cf-label-${item.id}`} aria-describedby={errs.length ? errId : undefined}>
                {ANSWERS.map((ans) => (
                  <button
                    key={ans}
                    type="button"
                    role="radio"
                    aria-checked={a.answer === ans}
                    disabled={ans === "na" && !item.naAllowed}
                    title={ans === "na" && !item.naAllowed ? t("cf.naNotAllowed") : undefined}
                    className={`cf-answer cf-answer-${ans}${a.answer === ans ? " is-selected" : ""}`}
                    onClick={() => {
                      patch(item.id, { answer: ans });
                      touch(item.id);
                    }}
                  >
                    {t(`checklist.answer.${ans}`)}
                  </button>
                ))}
              </div>
              {(failed || a.note || a.photos.length > 0) && (
                <div className="cf-extra">
                  <label htmlFor={noteId} className="cf-extra-label">
                    {t("cf.note")}
                    {!failed && <span className="field-optional"> · {t("form.optional")}</span>}
                  </label>
                  <textarea
                    id={noteId}
                    rows={2}
                    value={a.note}
                    maxLength={CHECKLIST_LIMITS.noteMax}
                    placeholder={t("cf.notePlaceholder")}
                    onChange={(e) => {
                      patch(item.id, { note: e.target.value });
                      touch(item.id);
                    }}
                  />
                  <div className="cf-photos">
                    {a.photos.map((p, n) => (
                      <div key={p.localId} className={`cf-photo${p.error ? " is-error" : ""}`}>
                        {p.preview ? <img src={p.preview} alt={t("cf.photoAlt", { code: item.code, n: n + 1 })} /> : <Icon name="check" size={18} />}
                        {p.uploading && <span className="cf-photo-status">{t("cf.photoUploading")}</span>}
                        {p.error && <span className="cf-photo-status">{t("error.invalid_photo")}</span>}
                        <button
                          type="button"
                          className="cf-photo-remove"
                          aria-label={t("cf.photoRemove")}
                          onClick={() => patch(item.id, (x) => ({ photos: x.photos.filter((q) => q.localId !== p.localId) }))}
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </div>
                    ))}
                    {a.photos.length < CHECKLIST_LIMITS.photosPerAnswer && (
                      <label className="cf-add-photo">
                        <Icon name="plus" size={16} />
                        {t("cf.addPhoto")}
                        <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="sr-only" onChange={(e) => void addPhoto(item, e)} />
                      </label>
                    )}
                  </div>
                </div>
              )}
              {errs.length > 0 && (
                <p id={errId} className="field-error" role="alert">
                  {errs.map((c) => t(`cf.issue.${c}`)).join(" ")}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      <div className="cf-bar">
        <span className="cf-progress" aria-live="polite">
          {t("cf.progress", { done: doneCount, total: items.length })}
          {missing > 0 && <span className="cf-missing"> · {t("cf.missing", { n: missing })}</span>}
        </span>
        {waitPhotos && (
          <span className="form-error" role="alert">
            {t("cf.waitPhotos")}
          </span>
        )}
        {submitError !== null && (
          <span className="form-error" role="alert">
            {t(errorKey(submitError))}
          </span>
        )}
        <button type="button" className="button button-primary cf-submit" disabled={busy} onClick={() => void submit()}>
          {busy ? t("cf.submitting") : t("cf.submit")}
        </button>
      </div>
    </>,
  );
}
